import React, { useEffect, useRef, useState } from "react";
import {
    View,
    Heading,
    Text,
    TextField,
    Flex,
    Divider,
    Item,
    NumberField,
    Checkbox,
    Picker,
    Dialog,
    Content,
    ToggleButton,
    ProgressCircle,
    ActionButton,
    ComboBox,
    ToastQueue,
    DatePicker
} from "@adobe/react-spectrum";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { TaskItem } from "../../../../services/soap/dataObjects/TaskItem";
import styles from "./add_update_task.module.css";
import { ArrowLeftLongIcon, ArrowRightLongIcon, CloseIcon } from "../../../../components/protected/Icons/IconsLib";
import TinyMCEEditor from "../../../../components/ui/TinyMCEEditor/TinyMCEEditior";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { CreateTaskRequest } from "../../../../services/soap/scheduler/requests/CreateTaskRequest";
import { generateTaskItem } from "../../../../services/soap/scheduler/requests/CreateTaskOptions";
import { parseDate } from '@internationalized/date';
import moment from "moment";
import { UpdateTaskRequest } from "../../../../services/soap/scheduler/requests/UpdateTaskRequest";
import { fromBase64Unicode, toBase64Unicode } from "../../../../utils/helperUtil";
import { addKey, useKeyboardShortcuts } from "../../../../hooks/UseKeyboardShortcuts";

interface IAddUpdateTaskProps {
    task: any;
    isEdit?: boolean;
    onClose?: () => void;
    refreshDetailView?: (data: any) => void;
    handleRefreshAddNew?: (data: any) => void;
    taskStatusList?: any;
    typeOfWorkList?: any;
    userList?: any;
    context?: any;
}

const AddUpdateTask: React.FC<IAddUpdateTaskProps> = ({
    task,
    isEdit,
    onClose,
    refreshDetailView,
    handleRefreshAddNew,
    taskStatusList,
    typeOfWorkList,
    userList,
    context }) => {

    const [formData, setFormData] = useState<any>({});
    const [reminderEnabled, setReminderEnabled] = useState(false);
    const [reminderValue, setReminderValue] = useState(30);
    const [reminderUnit, setReminderUnit] = useState<'Minutes' | 'Hours' | 'Days'>('Minutes');
    const [statusItems, setStatusItems] = useState(taskStatusList.filter((s: any) => s.title !== "") || []);
    const [showLoader, setShowLoader] = useState(false);
    const { t } = useTranslation('task');
    const _t = (key: string) => t(`add_update_task.${key}`, { ns: 'task' });
    const _tt = (key: string) => t(`task_common.${key}`, { ns: 'task' });
    const _m = (key: string) => t(`task_messages.${key}`, { ns: 'task' });
    const [notifyIds, setNotifyIds] = useState<Array<string | null>>([]);
    const { store, schedulerService } = useDependency();
    const [titleError, setTitleError] = useState<string | null>(null);
    const [typeError, setTypeError] = useState<string | null>(null);
    const [priorityError, setPriorityError] = useState<string | null>(null);
    const [statusError, setStatusError] = useState<string | null>(null);
    const [startEnabled, setStartEnabled] = useState<boolean>(false);
    const [deadlineEnabled, setDeadlineEnabled] = useState<boolean>(false);
    const originalSolverIdRef = useRef<string>("");


    useEffect(() => {
        if (isEdit) {
            originalSolverIdRef.current = String(task?.userId ?? "");
            const html = fromBase64Unicode(task.comments)
            const initial: string[] =
            Array.isArray(task?.GARANT) ? task.GARANT.map((g: any) => String(g.userId)) : task?.GARANT ? [String(task.GARANT.userId)] : [];
            setNotifyIds(initial.length ? initial : [null]);
            const mins = Number(task?.reminder ?? 0);
            setReminderEnabled(mins > 0);
            setReminderUnit('Minutes');
            setReminderValue(fromMinutes(mins, 'Minutes'));
            setFormData((prev: any) => ({ ...prev, reminder: String(mins) }));
            setFormData((prev: any) => ({
                ...prev,
                dateFrom: task.dateFrom ? moment(task.dateFrom).format('YYYY-MM-DD') : '',
                dateTo: task.dateTo ? moment(task.dateTo).format('YYYY-MM-DD') : '',
                comments: html,
                description: html
            }));
            if (task.reminder && task.reminder !== "0") {
                setReminderEnabled(true);
            }
            setFormData(task);
            setFormData((prev: any) => ({
                ...prev,
                comments: html
            }));
        } else {
            setNotifyIds([null]);
            const defaultTypeOfWork = typeOfWorkList.length ? typeOfWorkList.find((x: any) => +x.defaultFor > 0) : '';
            setFormData({
                // dateFrom: moment().format('YYYY-MM-DD'),
                // dateTo: moment(addDays(new Date(), 3)).format('YYYY-MM-DD'),
                solutionStatus: statusItems.length ? statusItems[0].ident : '',
                userId: store.UserId.toString(),
                solver: store.UserId.toString(),
                entryType: defaultTypeOfWork ? defaultTypeOfWork.ident : '',
                priority: '0',
                reminder: '0',
                activityId: context?.activityId || '',
                projectId: context?.projectId || '',
                folderId: context?.folderId || '',
            })
        }
    }, [task]);

    useEffect(() => {
        const selected = notifyIds.filter((x): x is string => !!x);
        setFormData((prev: any) => ({
            ...prev,
            GARANT: selected.map((id) => ({ userId: id })) as any[]
        }));
    }, [notifyIds]);

    const addIdToNotify = (idToAdd: string) => {
        if (!idToAdd) return;

        setNotifyIds((prev) => {
            const next = [...prev];

            // already present? do nothing
            if (next.some((x) => x === idToAdd)) return ensureTrailingEmpty(next);

            // put into first empty slot if exists, else append
            const emptyIndex = next.findIndex((x) => x === null);
            if (emptyIndex !== -1) next[emptyIndex] = idToAdd;
            else next.push(idToAdd);

            return ensureTrailingEmpty(next);
        });
    };


    const ensureTrailingEmpty = (next: Array<string | null>) => {
        const selectedCount = next.filter(Boolean).length;
        const canSelectMore = (userList?.length ?? 0) > selectedCount;
        const hasEmpty = next.some((x) => x === null);

        if (canSelectMore && !hasEmpty) next.push(null);
        if (!canSelectMore) {
            // remove any empties if all users are selected
            return next.filter((x) => x !== null);
        }
        return next;
    };

    const handleNotifyChange = (index: number, id: string) => {
        setNotifyIds((prev) => {
            const next = [...prev];
            next[index] = id;
            return ensureTrailingEmpty(next);
        });
    };

    const handleRemoveNotify = (index: number) => {
        setNotifyIds((prev) => {
            const next = prev.filter((_, i) => i !== index);
            // Always keep at least one picker
            if (next.length === 0) next.push(null);
            return ensureTrailingEmpty(next);
        });
    };

    const itemsForIndex = (index: number) => {
        const selectedElsewhere = new Set(
            notifyIds.filter((x, i) => i !== index && x).map(String)
        );
        return (userList ?? []).filter((u: any) => {
            const id = String(u.id);
            return !selectedElsewhere.has(id) || notifyIds[index] === id;
        });
    };

    const priorityItems = [
        { id: "-2", name: _tt('highest_priority'), color: '#F72B1C' },
        { id: "-1", name: _tt('high_priority'), color: '#FF9800' },
        { id: "0", name: _tt('normal_priority'), color: '#8BC34A' },
        { id: "1", name: _tt('low_priority'), color: '#14AFF5' },
        { id: "2", name: _tt('lowest_priority'), color: '#8F9FA6' }
    ];  

    const getPriority = (id: string) => {
        const priority = priorityItems.find(p => p.id === id);
        return priority;
    }

    const addDays = (d: Date, days: number) => {
        const x = new Date(d);
        x.setDate(x.getDate() + days);
        return x;
    };

    const onSubmit = async () => {
        const title = (formData.title ?? '').trim();
        const type = (formData.entryType ?? '').trim();
        const priority = (formData.priority ?? '').trim?.() ?? formData.priority ?? '';
        const status = (formData.solutionStatus ?? '').trim?.() ?? formData.solutionStatus ?? '';
        let missing: string[] = [];

        if (!title) {
            setTitleError('Title is required');
            missing.push('Title');
        }
        if (!type) {
            setTypeError('Type is required');
            missing.push('Type');
        }
        if (!priority) {
            setPriorityError('Priority is required');
            missing.push('Priority');
        }
        if (!status) {
            setStatusError('Status is required');
            missing.push('Status');
        }

        if (missing.length) {
            ToastQueue.negative(`Please fill in: ${missing.join(', ')}.`, { timeout: 3000 });
            return; // stop submit
        }
        try {
            setShowLoader(true);
            const request = {
                ...generateTaskItem(
                    formData.activityId || '',
                    formData.projectId || '',
                    formData.folderId || '',
                    formData.userId || store.UserId,
                    formData.title || '',
                    formData.entryType || '',
                    formData.userId || store.UserId,
                    formData.solutionStatus || '',
                    {}
                ), GARANT: formData.GARANT || [],
                dateFrom: formData.dateFrom || moment().format('YYYY-MM-DD'),
                dateTo: formData.dateTo || moment(addDays(new Date(), 3)).format('YYYY-MM-DD'),
                priority: formData.priority || '0',
                comments: formData.comments ? toBase64Unicode(formData.comments) : '',
                description: formData.description ? toBase64Unicode(formData.description) : '',
                reminder: formData.reminder || '0',
            };

            if (!isEdit) {
                let { result, EXCEPTION, commonId, headId, name } = await schedulerService.createTask(new CreateTaskRequest(store.Server, store.SessionId, request));

                if (result === "OK") {
                    ToastQueue.positive(isEdit ? _m('task_updated') : _m('task_created'), { timeout: 3000 });
                    refreshDetailView && refreshDetailView(request);
                    handleRefreshAddNew && handleRefreshAddNew({ ...request, ident: headId });
                    onClose && onClose();
                } else {
                    ToastQueue.negative(EXCEPTION?.message || "Error in submitting form data", { timeout: 3000 });
                    console.error("Error in submitting form data:", EXCEPTION?.message);
                }
            } else {
                const payloadTaskItem: TaskItem = {
                    ...task,
                    ident: formData.ident,
                    userId: formData.userId || store.UserId,
                    title: formData.title || '',
                    entryType: formData.entryType || '',
                    solver: formData.userId || '',
                    solutionStatus: formData.solutionStatus || '',
                    GARANT: formData.GARANT || [],
                    dateFrom: formData.dateFrom || moment().format('YYYY-MM-DD'),
                    dateTo: formData.dateTo || moment(addDays(new Date(), 3)).format('YYYY-MM-DD'),
                    comments: formData.comments ? toBase64Unicode(formData.comments) : '',
                    description: formData.description ? toBase64Unicode(formData.description) : '',
                    reminder: formData.reminder || '0',
                    priority: formData.priority || '0',
                    USER: { id: formData.userId || store.UserId.toString() },
                }
                console.log('payloadTaskItem ', payloadTaskItem);
                let { result, EXCEPTION } = await schedulerService.updateTask(new UpdateTaskRequest(store.Server, store.SessionId, payloadTaskItem));

                if (result === "OK") {
                    ToastQueue.positive(isEdit ? "Task updated successfully" : "Task created successfully", { timeout: 3000 });
                    refreshDetailView && refreshDetailView(payloadTaskItem);
                    onClose && onClose();
                } else {
                    ToastQueue.negative(EXCEPTION?.message || "Error in submitting form data", { timeout: 3000 });
                    console.error("Error in submitting form data:", EXCEPTION?.message);
                }
            }
        } catch (error: any) {
            ToastQueue.negative(error.message || "Error in submitting form data", { timeout: 3000 });
            console.error("Error in submitting form data:", error.message);
        }
        finally {
            setShowLoader(false);
        }
    };

    // helpers
    const toMinutes = (value: number, unit: 'Minutes' | 'Hours' | 'Days') => {
        if (!value || value <= 0) return 0;
        if (unit === 'Days') return value * 24 * 60;
        if (unit === 'Hours') return value * 60;
        return value; // Minutes
    };

    const fromMinutes = (mins: number, unit: 'Minutes' | 'Hours' | 'Days') => {
        if (!mins || mins <= 0) return 0;
        if (unit === 'Days') return Math.floor(mins / (24 * 60));
        if (unit === 'Hours') return Math.floor(mins / 60);
        return mins; // Minutes
    };

    useKeyboardShortcuts([...addKey('enter', { ctrl: true, shift: true }, () => onSubmit())], true);

    return (
        <Dialog
            width={{ base: "100%", M: "800px" }}
            height={{ base: "100%", M: "100%" }}
            isDismissable
            onDismiss={onClose} aria-label={isEdit ? _t('edit_task') : _t('add_task')}
            UNSAFE_style={{ background: '#fff' }}>
            <Heading>{isEdit ? _t('edit_task') : _t('add_task')}</Heading>
            <Content>
                <View
                    UNSAFE_style={{
                        margin: "0 auto",
                    }}
                >
                    <Flex height={{L:'34px',base:'auto'}} direction={'row'} marginTop={8} alignItems={'center'} gap={8}>
                        {/* <Text width={'60px'} UNSAFE_style={{ fontWeight: 600 }}>Title:</Text> */}
                        <TextField
                            label={_t("title")}
                            aria-label={_t("title")}
                            labelPosition="side"
                            isRequired
                            value={formData.title || ''}
                            onChange={(e) => {
                                if (titleError) setTitleError(null);
                                setFormData({ ...formData, title: e });
                            }}
                            width="100%"
                            marginTop={8}
                            UNSAFE_className="no-bottom-border-input"
                            isQuiet
                            validationState={titleError ? 'invalid' : undefined}
                            errorMessage={titleError || undefined}
                        />

                    </Flex>
                    <Divider size="M" UNSAFE_style={{ marginTop: '5px' }} />
                    {/* Context (breadcrumbs) */}
                    <Flex height={{L:'34px',base:'auto'}} direction={{ base: "column", L: "row" }} marginTop={8} alignItems={'center'} gap={8}>
                        {/* <Text width={'60px'} UNSAFE_style={{ fontWeight: 600 }}>Context:</Text> */}
                        <TextField
                            label={_t("context")}
                            labelPosition="side"
                            aria-label={_t("context")}
                            width={{ base: '100%', L: '60%' }}
                            isQuiet
                            UNSAFE_className="no-bottom-border-input" />
                        <ComboBox
                            label={_t("type")}  
                            labelPosition="side"
                            isRequired
                            isQuiet
                            UNSAFE_className="no-bottom-border-input"
                            width={{ base: "100%", L: "40%" }}
                            defaultItems={typeOfWorkList}
                            selectedKey={formData.entryType}
                            onSelectionChange={(key) => {
                                if (typeError) setTypeError(null);
                                setFormData({ ...formData, entryType: key?.toString() || "" });
                            }}
                            validationState={typeError ? "invalid" : undefined}
                            errorMessage={typeError || undefined}
                        >
                            {(item: any) => (
                                <Item key={item.ident} textValue={String(item.title ?? "")}>
                                    {item.title}
                                </Item>
                            )}
                        </ComboBox>

                    </Flex>
                    <Divider size="M" UNSAFE_style={{ marginTop: '5px' }} />
                    <Flex height={{L:notifyIds.length === 1 ? '34px' : 'auto', base:'auto'}} direction={{ base: "column", M: "row" }} gap={8} marginTop={8}>
                        <ComboBox
                            label={_t("solver")}
                            labelPosition="side"
                            aria-label={_t("solver")}
                            isQuiet
                            UNSAFE_className="no-bottom-border-input"
                            width={{ base: "100%", M: "50%" }}
                            defaultItems={userList ?? []}
                            selectedKey={formData.userId || undefined}
                            onSelectionChange={(key) => {
                                // setFormData({ ...formData, userId: key?.toString() || "" });
                                const nextSolverId = key?.toString() || "";
                                setFormData({ ...formData, userId: nextSolverId });

                                if (isEdit) {
                                    const originalSolverId = originalSolverIdRef.current;
                                    if (originalSolverId && nextSolverId && nextSolverId !== originalSolverId) {
                                        addIdToNotify(originalSolverId);
                                    }
                                }
                            }}
                        >
                            {(item: any) => (
                                <Item key={item.id} textValue={String(item.name ?? "")}>
                                    {String(item.name ?? "")}
                                </Item>
                            )}
                        </ComboBox>

                        <Flex direction="column" gap={8} flex>
                            {notifyIds.map((val, i) => (
                                <Flex key={`notify-${i}`} direction="row" gap={8} alignItems="center">
                                    {!!val && (
                                        <ActionButton
                                            aria-label={`Remove notify ${i + 1}`}
                                            isQuiet
                                            onPress={() => handleRemoveNotify(i)}
                                            UNSAFE_style={{ minWidth: 32, height: 32 }}
                                        >
                                            <CloseIcon size={16} />
                                        </ActionButton>
                                    )}

                                    <ComboBox
                                        label={_t("notify")}
                                        labelPosition="side"
                                        aria-label={_t("notify")}
                                        isQuiet
                                        UNSAFE_className="no-bottom-border-input"
                                        width="100%"
                                        defaultItems={itemsForIndex(i) ?? []}
                                        selectedKey={val ? val : undefined}
                                        onSelectionChange={(key) => {
                                            handleNotifyChange(i, key?.toString() || "");
                                        }}
                                    >
                                        {(item: any) => (
                                            <Item key={item.id} textValue={String(item.name ?? "")}>
                                                {String(item.name ?? "")}
                                            </Item>
                                        )}
                                    </ComboBox>
                                </Flex>
                            ))}
                        </Flex>
                    </Flex>

                    <Divider size="M" UNSAFE_style={{ marginTop: '5px' }} />
                    {/* Priority */}
                    <Flex height={'34px'} direction={'row'} marginTop={8} alignItems={'center'} gap={8} UNSAFE_className={styles.priority_contaciner}>
                        <Text width={'60px'} UNSAFE_style={{ fontWeight: 400 }}>{_t("priority")} *</Text>
                        {priorityItems.map((s: any) => {
                            const isSelected = formData.priority === s.id;
                            return (
                                <React.Fragment key={s.id}>
                                    <ToggleButton
                                        isSelected={isSelected}
                                        onChange={() => {
                                            if (priorityError) setPriorityError(null);
                                            setFormData({ ...formData, priority: s.id })
                                        }}
                                        aria-invalid={!!priorityError}
                                        UNSAFE_style={{ background: isSelected ? s.color : '#ECEFF1', borderColor: isSelected && s.color }}
                                    >
                                        <Text>{s.name}</Text>
                                    </ToggleButton>
                                </React.Fragment>
                            );
                        })}
                    </Flex>
                    {priorityError && (
                        <Text UNSAFE_style={{ color: '#E34850', marginTop: 4, marginLeft: 60 }}>{priorityError}</Text>
                    )}

                    <Divider size="M" UNSAFE_style={{ marginTop: '5px' }} />
                    <Flex height={{L:startEnabled && deadlineEnabled && reminderEnabled ?'auto':'34px',base:'auto'}} direction="column" gap={8} marginTop={8} width="100%">
                        {/* <Text UNSAFE_style={{ fontWeight: 400 }}>When:</Text> */}
                        <Flex
                            direction="row"
                            wrap
                            gap={16}
                            alignItems="center"
                            justifyContent="start"
                            width="100%"
                        >
                            {/* START */}
                            <Flex alignItems="center" gap={8}>
                                <Checkbox
                                    isSelected={startEnabled}
                                    onChange={(enabled) => {
                                        setStartEnabled(enabled);
                                        if (enabled) {
                                            setFormData({
                                                ...formData,
                                                dateFrom: moment().format("YYYY-MM-DD"),
                                            });
                                        }
                                    }}
                                >
                                    {_t("start")}
                                </Checkbox>
                                {startEnabled && (
                                    <DatePicker
                                        aria-label={_t("start")}
                                        value={
                                            formData.dateFrom
                                                ? parseDate(moment(formData.dateFrom).format("YYYY-MM-DD"))
                                                : undefined
                                        }
                                        onChange={(e) =>
                                            setFormData({ ...formData, dateFrom: e?.toString() })
                                        }
                                    />
                                )}
                            </Flex>

                            {/* DEADLINE */}
                            <Flex alignItems="center" gap={8}>
                                <Checkbox
                                    isSelected={deadlineEnabled}
                                    onChange={(enabled) => {
                                        setDeadlineEnabled(enabled);
                                        if (enabled) {
                                            setFormData({
                                                ...formData,
                                                dateTo: moment(addDays(new Date(), 3)).format("YYYY-MM-DD"),
                                            });
                                        }
                                    }}
                                >
                                    {_t("deadline")}
                                </Checkbox>
                                {deadlineEnabled && (
                                    <DatePicker
                                        aria-label={_t("deadline")}
                                        value={
                                            formData.dateTo
                                                ? parseDate(moment(formData.dateTo).format("YYYY-MM-DD"))
                                                : undefined
                                        }
                                        onChange={(e) =>
                                            setFormData({ ...formData, dateTo: e?.toString() })
                                        }
                                    />
                                )}
                            </Flex>

                            {/* REMINDER */}
                            <Flex alignItems="center" gap={8} wrap>
                                <Checkbox
                                    isSelected={reminderEnabled}
                                    onChange={(enabled) => {
                                        setReminderEnabled(enabled);
                                        setFormData((prev: any) => ({
                                            ...prev,
                                            reminder: enabled
                                                ? String(toMinutes(reminderValue, reminderUnit))
                                                : "0",
                                        }));
                                    }}
                                >
                                    {_t("reminder")}
                                </Checkbox>

                                {reminderEnabled && (
                                    <Flex alignItems="center" gap={8} wrap>
                                        <NumberField
                                            aria-label={_t("reminder")}
                                            value={reminderValue}
                                            onChange={(val) => {
                                                const v = Number(val) || 0;
                                                setReminderValue(v);
                                                if (reminderEnabled) {
                                                    setFormData((prev: any) => ({
                                                        ...prev,
                                                        reminder: String(toMinutes(v, reminderUnit)),
                                                    }));
                                                }
                                            }}
                                            minValue={0}
                                            width={100}
                                        />

                                        <ToggleButton
                                            UNSAFE_style={{ marginLeft: '20px' }}
                                            isSelected={reminderUnit === "Minutes"}
                                            onChange={() => {
                                                setReminderUnit("Minutes");
                                                const mins = Number(formData.reminder ?? 0);
                                                setReminderValue(fromMinutes(mins, "Minutes"));
                                            }}
                                        >
                                            <Text>{_t("minutes")}</Text>
                                        </ToggleButton>

                                        <ToggleButton
                                            isSelected={reminderUnit === "Hours"}
                                            onChange={() => {
                                                setReminderUnit("Hours");
                                                const mins = Number(formData.reminder ?? 0);
                                                setReminderValue(fromMinutes(mins, "Hours"));
                                            }}
                                        >
                                            <Text>{_t("hours")}</Text>
                                        </ToggleButton>

                                        <ToggleButton
                                            isSelected={reminderUnit === "Days"}
                                            onChange={() => {
                                                setReminderUnit("Days");
                                                const mins = Number(formData.reminder ?? 0);
                                                setReminderValue(fromMinutes(mins, "Days"));
                                            }}
                                        >
                                            <Text>{_t("days")}</Text>
                                        </ToggleButton>
                                    </Flex>
                                )}
                            </Flex>
                        </Flex>
                    </Flex>

                    <Divider size="M" UNSAFE_style={{ marginTop: '5px' }} />

                    {/* Status */}
                    <View marginTop={8} UNSAFE_className={styles.status_container}>
                        {/* <Text UNSAFE_style={{ fontWeight: 400 }}>Status *</Text> */}
                        <Flex
                            marginTop={18}
                            alignItems="center"
                            gap="size-100"
                            UNSAFE_className="status-toggles"
                            aria-invalid={!!statusError}>
                            {statusItems.map((s: any, i: number) => {
                                const isSelected = formData.solutionStatus === s.ident;
                                const isSecondLast = i === statusItems.length - 2;
                                return (
                                    <React.Fragment key={s.ident}>
                                        <ToggleButton
                                            isSelected={isSelected}
                                            onChange={() => {
                                                if (statusError) setStatusError(null);
                                                setFormData({ ...formData, solutionStatus: s.ident })
                                            }}
                                            UNSAFE_className={isSelected ? styles.status_button_selected : styles.status_button}
                                        >
                                            <Flex alignItems="center" gap={2}>
                                                <View UNSAFE_className={styles.dot} UNSAFE_style={{ backgroundColor: s.color }} />
                                                <Text>{s.title}</Text>
                                            </Flex>
                                        </ToggleButton>
                                        {i < statusItems.length - 2 && <ArrowRightLongIcon size={18} />}
                                        {isSecondLast && <ArrowLeftLongIcon size={18} />}
                                    </React.Fragment>
                                );
                            })}
                        </Flex>
                        {statusError && (
                            <Text UNSAFE_style={{ color: '#E34850', marginTop: 4 }}>{statusError}</Text>
                        )}
                    </View>
                    <View marginTop={8}>
                        <Flex
                            direction={'column'}
                            position={'relative'}
                            width={'100%'}
                            height={'100%'}
                            alignItems={'start'}
                            justifyContent={'start'}
                            zIndex={0}
                            flex={1}
                            minHeight={'200px'}
                            gridRow={'20'}>
                            <TinyMCEEditor
                                key={'tiny_editor_mobile'}
                                initialValue={formData.comments || ''}
                                onChange={(e: any) => {
                                    setFormData({ ...formData, comments: e });
                                }}
                                height={'200px'}
                            />
                        </Flex>
                    </View>
                    <Flex
                        UNSAFE_style={{
                            backgroundColor: 'white',
                            justifyContent: 'end',
                            width: '100%',
                            marginTop: '10px',
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 1
                        }}
                    >
                        <Flex gap={'size-200'} UNSAFE_className={styles.button_container}>
                            <ProgressCircle aria-label='....Loading' isHidden={!showLoader} isIndeterminate UNSAFE_style={{ marginRight: 20 }} />
                            <Button variant='danger' onClick={onClose} className={`${styles.btn} ${styles.btn_cancel}`}>
                                {_t('cancel')}
                            </Button>
                            <Button type='submit' onClick={() => onSubmit()} className={`${styles.btn} ${styles['btn-add']}`}>
                                {isEdit ? _t("update") : _t("save")}
                            </Button>
                        </Flex>
                    </Flex>
                </View>
            </Content>
        </Dialog>
    );
}

export default AddUpdateTask;
